import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import LoadingOutlined from '@ant-design/icons/lib/icons/LoadingOutlined';

import { OpSpin } from 'new-components/DLS/OpSpin/OpSpin';
import { OpDrawer } from 'new-components/DLS/OpDrawer/OpDrawer';
import { OpTabs } from 'new-components/DLS/OpTabs/OpTabs';
import { useSelectorJs } from 'utils/customHooks';
import { useEndpointPermissions } from 'utils/customHooks/useEndpointPermissions';
import { useValidateScopes } from 'utils/customHooks/useValidateScopes';
import { getOpParams } from 'utils/getOpParams';
import { heliumReceive } from 'utils/helpers';

import { DRAWER_WIDTH } from '../../../constants/ui';
import { VisitsState } from '../../../store/slices/visitsSlice';
import { ImmutableRootState } from '../../../store/store';

import VisitorsDrawerFooter from './footer/VisitorsDrawerFooter';
import VisitorsDrawerHeader from './header/VisitorsDrawerHeader';
import DetailsTab from './tabs/DetailsTab';
import GuestPassLogTab from './tabs/GuestPassLogTab';
import GuestPassTab from './tabs/GuestPassTab';
import NotesTab from './tabs/NotesTab';
import VisitorsTab from './tabs/VisitorsTab';

interface VisitorsDrawerProps {
  open: boolean;
  onClose: () => void;
  tab?: string;
  zIndex?: number;
}

interface IntegrationInfo {
  msiAltaOrgId?: string | null;
  guestPassEnabled?: number;
}

const VisitorsDrawer = ({
  open,
  onClose,
  tab,
  zIndex,
}: VisitorsDrawerProps) => {
  const { t } = useTranslation();

  const { orgId } = getOpParams();

  const { isValid: hasVisitorlogRead } = useValidateScopes([
    'o{orgId}-visitorVisitorLog:r',
    'o{orgId}-visitorVisitors:r',
    'o:r',
    's-o:r',
  ]);

  const { isValid: hasGuestpassRead } = useValidateScopes([
    'o{orgId}-visitorGuestPass:r',
    'o{orgId}-visitorVisitors:r',
    'o:r',
    's-o:r',
  ]);
  // Only fetch the MSI Alta integration when the user has a scope helium's
  // GET /integrationMsiAlta route accepts, otherwise it 403s ("method that
  // get a integration").
  const { hasReadPermission: hasMsiAltaIntegrationRead } =
    useEndpointPermissions('getIntegrationMsiAlta');

  const selectedVisit = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['visits', 'selectedVisit']),
  ) as VisitsState['selectedVisit'];

  const [isIntegrated, setIsIntegrated] = useState<boolean>(false);
  const [isTokenValid, setIsTokenValid] = useState<boolean>(false);
  const [activeKey, setActiveKey] = useState<string>('visitors');
  const [loading, setLoading] = useState<boolean>(false);

  // Reset the active tab to 'visitors' when the drawer opens
  useEffect(() => {
    if (open) {
      setActiveKey(tab || 'visitors');
    }

    const checkIntegration = async () => {
      if (!hasMsiAltaIntegrationRead) {
        setIsIntegrated(false);
        setIsTokenValid(false);
        return;
      }
      setLoading(true);
      try {
        const integrationResponse = await heliumReceive(
          'getIntegrationMsiAlta',
          [orgId],
        );
        const integrationData = Array.isArray(integrationResponse?.data)
          ? (integrationResponse.data as IntegrationInfo[])
          : [];
        const initialData = integrationData[0];
        if (initialData) {
          const { msiAltaOrgId, guestPassEnabled } = initialData;

          const hasOrgId = !!msiAltaOrgId;
          const guestPassEnabledCondition = guestPassEnabled === 1;

          // Set isIntegrated to true if all conditions are met
          if (hasOrgId && guestPassEnabledCondition) {
            setIsIntegrated(true);
            setIsTokenValid(true);
          } else {
            setIsIntegrated(false);
            setIsTokenValid(false);
          }
        } else {
          setIsIntegrated(false);
          setIsTokenValid(false);
        }
      } catch {
        setIsIntegrated(false);
        setIsTokenValid(false);
      } finally {
        setLoading(false);
      }
    };
    checkIntegration();
  }, [orgId, tab, open, selectedVisit?.id, hasMsiAltaIntegrationRead]);

  const tabItems = [
    {
      key: 'visitors',
      label: t('visitors.tabs.visitors'),
      children: <VisitorsTab />,
    },
    {
      key: 'details',
      label: t('visitors.tabs.details'),
      children: <DetailsTab />,
    },
    ...(hasVisitorlogRead
      ? [
          {
            key: 'notes',
            label: t('visitors.tabs.notes'),
            children: <NotesTab isActive={activeKey === 'notes'} />,
          },
        ]
      : []),
    ...(hasGuestpassRead && isIntegrated && isTokenValid
      ? [
          {
            key: 'guestPass',
            label: t('visitors.tabs.guestPass'),
            children: <GuestPassTab />,
          },
        ]
      : []),
    ...(hasGuestpassRead && isIntegrated && isTokenValid
      ? [
          {
            key: 'guestPassLog',
            label: t('visitors.tabs.guestPassLog'),
            children: <GuestPassLogTab />,
          },
        ]
      : []),
  ];

  return (
    <OpDrawer
      title={t('visitors.title')}
      size={DRAWER_WIDTH}
      open={open}
      onClose={onClose}
      zIndex={zIndex}
      extra={
        <VisitorsDrawerHeader
          onClose={onClose}
          setLoading={setLoading}
          loading={loading}
        />
      }
      footer={
        <VisitorsDrawerFooter setLoading={setLoading} loading={loading} />
      }
    >
      {!open || !selectedVisit?.id ? null : loading ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <OpSpin
            indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
          />
        </div>
      ) : (
        <OpTabs
          activeKey={activeKey}
          onChange={setActiveKey}
          items={tabItems}
        />
      )}
    </OpDrawer>
  );
};

export default VisitorsDrawer;
